import shallowEqual from 'react-utils/shallowEqual';
import { arrangeNodePositionSubsetById, getBoundsFuncForConfig, getElementsFuncForConfig, getTreeRootElementFuncForConfig
// @ts-expect-error no types
} from './nodePositionUtils';
// @ts-expect-error no types
import { getComputedMargin } from '../dom/margin';
import { isSectionNode, isContainerNode, isModuleNode } from 'layout-data-lib/GridLayoutDataTree/utils';
import { layoutNodeTypes } from 'layout-dnd-utils/constants/dndAreaHoverPositionConstants';
import { isUnifiedGap } from 'layout-data-lib/GridLayoutDataTree/typenameUtils';
import { getPegboardRowSize } from '../geometry/pegboard';
const getGridLayoutTreePosition = (id, bounds, margin, activeBreakpoint) => {
  return Object.assign({}, bounds, {
    type: layoutNodeTypes.GRID_LAYOUT_ROOT,
    id,
    depth: 0,
    isGridCell: false,
    margin: {
      top: margin.top,
      left: margin.left
    },
    gridLayoutId: undefined,
    activeBreakpoint
  });
};
const getGridGapSizes = (gridNode, activeBreakpoint, scale) => {
  const {
    gap: gapDefinition
  } = gridNode.getBreakpointStyles(activeBreakpoint);
  if (!gapDefinition) {
    return {
      rowGap: 0,
      columnGap: 0
    };
  }
  if (isUnifiedGap(gapDefinition)) {
    return {
      rowGap: gapDefinition.size.value * scale,
      columnGap: gapDefinition.size.value * scale
    };
  }
  return {
    rowGap: gapDefinition.rowGap.value * scale,
    columnGap: gapDefinition.columnGap.value * scale
  };
};
export const getGridTemplateTrackSize = ({
  gridNode,
  pegboardWidth,
  activeBreakpoint,
  scale,
  previewWidth
}) => {
  const {
    columns
  } = gridNode.getContainerLayout(activeBreakpoint);
  const {
    rowGap,
    columnGap
  } = getGridGapSizes(gridNode, activeBreakpoint, scale);
  const trackSizes = {
    rowSize: 0,
    columnSize: 0,
    rowGap,
    columnGap
  };
  trackSizes.rowSize = getPegboardRowSize(activeBreakpoint, previewWidth, scale);
  if (columns.length > 0 && pegboardWidth > 0) {
    const totalColumnGap = trackSizes.columnGap * (columns.length - 1);
    trackSizes.columnSize = (pegboardWidth - totalColumnGap) / columns.length;
  }
  return trackSizes;
};
const gridNodeExtractorBuilder = (nodePositions, previousNodePositionsForAreaById, {
  activeBreakpoint,
  getBoundsForConfig,
  getElementsForConfig,
  gridLayoutRootElement,
  scale,
  previewWidth
}) => {
  const gridNodeExtractor = ({
    gridNode,
    depth
  }) => {
    const prevPosition = previousNodePositionsForAreaById[gridNode.getId()];
    const gridLayoutId = gridNode.getRootId();
    const gridNodeElement = getElementsForConfig(gridNode, gridLayoutRootElement);
    const bounds = getBoundsForConfig(gridNodeElement[0]);
    const parentIdForNode = gridNode.getParentNodeId();
    const parentNodePosition = nodePositions.find(node => node.id === parentIdForNode);
    let newNodePosition;
    if (isSectionNode(gridNode)) {
      newNodePosition = Object.assign({}, bounds, {
        depth: 1,
        gridLayoutId: gridNode.getRootId(),
        id: gridNode.getId(),
        hasLayoutStyles: gridNode.hasStyles(),
        numChildren: gridNode.hasChildNodeIds() ? gridNode.getChildNodeIds().length : 0,
        parentId: gridNode.getRootId(),
        type: layoutNodeTypes.GRID_LAYOUT_SECTION,
        isGridCell: false,
        activeBreakpoint
      });
    } else if (isContainerNode(gridNode) && !gridNode.isPegboard(activeBreakpoint)) {
      let gridLayoutContainerType;
      if (gridNode.isHorizontalStack(activeBreakpoint)) {
        gridLayoutContainerType = layoutNodeTypes.GRID_LAYOUT_HORIZONTAL_STACK;
      } else if (gridNode.isVerticalStack(activeBreakpoint)) {
        gridLayoutContainerType = layoutNodeTypes.GRID_LAYOUT_VERTICAL_STACK;
      } else {
        gridLayoutContainerType = layoutNodeTypes.GRID_LAYOUT_GRID;
      }
      newNodePosition = Object.assign({}, bounds, {
        depth,
        gridLayoutId,
        id: gridNode.getId(),
        hasLayoutStyles: gridNode.hasStyles(),
        numChildren: gridNode.hasChildNodeIds() ? gridNode.getChildNodeIds().length : 0,
        parentId: parentIdForNode,
        type: gridLayoutContainerType,
        isGridCell: (parentNodePosition === null || parentNodePosition === void 0 ? void 0 : parentNodePosition.type) === layoutNodeTypes.GRID_LAYOUT_GRID,
        activeBreakpoint
      });
    } else if (isContainerNode(gridNode) && gridNode.isPegboard(activeBreakpoint)) {
      const layout = gridNode.getContainerLayout(activeBreakpoint);
      newNodePosition = Object.assign({}, bounds, getGridTemplateTrackSize({
        gridNode,
        pegboardWidth: bounds.width,
        activeBreakpoint,
        scale,
        previewWidth
      }), {
        depth,
        gridLayoutId,
        id: gridNode.getId(),
        hasLayoutStyles: gridNode.hasStyles(),
        numChildren: gridNode.hasChildNodeIds() ? gridNode.getChildNodeIds().length : 0,
        parentId: parentIdForNode,
        type: layoutNodeTypes.PEGBOARD,
        isGridCell: false,
        rows: layout.rows.length,
        columns: layout.columns.length,
        activeBreakpoint
      });
    } else if (isModuleNode(gridNode)) {
      newNodePosition = Object.assign({}, bounds, {
        depth,
        gridLayoutId,
        // TODO: isFixedPosition and isAbsolutePosition only get set when
        // computing bounds with applyPositionInfo. Consider toggling that on
        // after validating against perf concerns with getComputedStyles. We
        // also may need to split up the method used for bounds calculation
        // between modules and other grid content types.
        isFixedPosition: false,
        isAbsolutePosition: false,
        id: gridNode.getId(),
        hasLayoutStyles: gridNode.hasStyles(),
        parentId: gridNode.getParentNodeId(),
        type: layoutNodeTypes.GRID_LAYOUT_MODULE,
        inPegboard: false,
        isGridCell: (parentNodePosition === null || parentNodePosition === void 0 ? void 0 : parentNodePosition.type) === layoutNodeTypes.GRID_LAYOUT_GRID,
        activeBreakpoint
      });
      const {
        gridSpanning
      } = gridNode.getEditorProvidedBreakpointStyles(activeBreakpoint);
      if ((parentNodePosition === null || parentNodePosition === void 0 ? void 0 : parentNodePosition.type) === layoutNodeTypes.PEGBOARD && gridSpanning) {
        newNodePosition.rowsSpanned = gridSpanning.rowsSpanned;
        newNodePosition.columnsSpanned = gridSpanning.columnsSpanned;
        newNodePosition.inPegboard = true;
      }
    } else {
      throw new Error('Attempting to get the node position of an invalid grid content type');
    }
    if (prevPosition && prevPosition.id === gridNode.getId() && shallowEqual(prevPosition, newNodePosition)) {
      nodePositions.push(prevPosition);
    } else if (newNodePosition) {
      nodePositions.push(newNodePosition);
    }
    return {
      id: gridNode.getId(),
      nodeElement: gridNodeElement[0]
    };
  };
  return gridNodeExtractor;
};
const calculateGridLayoutTreeNodePositions = (gridLayoutIds, gridLayoutIteratorMap, previousNodePositions = {}, subConfig = {}, activeBreakpoint = 'default', scale = 1, previewWidth) => {
  const newNodePositions = {};
  if (subConfig.startCallback) {
    subConfig.startCallback(subConfig);
  }
  const getBoundsForConfig = getBoundsFuncForConfig(subConfig);
  const getElementsForConfig = getElementsFuncForConfig(subConfig);
  const previousNodePositionsById = arrangeNodePositionSubsetById(previousNodePositions, subConfig);
  const getTreeRootForConfig = getTreeRootElementFuncForConfig(subConfig);
  gridLayoutIds.forEach(id => {
    const gridLayoutRootElement = getTreeRootForConfig(id);
    const newBounds = getBoundsForConfig(gridLayoutRootElement);
    const newMargin = getComputedMargin(gridLayoutRootElement);
    const layoutTreePosition = getGridLayoutTreePosition(id, newBounds, newMargin, activeBreakpoint);
    const previousNodePositionsForArea = previousNodePositions[id] || [];
    const previousRootPosition = previousNodePositionsForArea[0];
    const previousNodePositionsForAreaById = previousNodePositionsById[id] || {};
    const newNodePositionsForArea = [];

    // Manually do a shallow equal check on the nested margin property (that is the only nested
    // thing on a root position that wouldn't be correctly checked by a single `shallowEqual` call)
    if (previousRootPosition && previousRootPosition.type === layoutNodeTypes.GRID_LAYOUT_ROOT && shallowEqual(previousRootPosition.margin, layoutTreePosition.margin)) {
      layoutTreePosition.margin = previousRootPosition.margin;
    }
    if (previousRootPosition && previousRootPosition.id === id && shallowEqual(previousRootPosition, layoutTreePosition)) {
      newNodePositionsForArea.push(previousRootPosition);
    } else {
      newNodePositionsForArea.push(layoutTreePosition);
    }
    if (gridLayoutIteratorMap) {
      gridLayoutIteratorMap[id]({
        gridNodeCallback: gridNodeExtractorBuilder(newNodePositionsForArea, previousNodePositionsForAreaById, {
          getBoundsForConfig,
          getElementsForConfig,
          gridLayoutRootElement,
          activeBreakpoint,
          previewWidth,
          scale
        })
      });
    }

    // Compare if the node position stack for this specific layout section is the same has before.
    // And if it is return the previous position array reference (this works because `rowExtractorBuilder`
    // and `columnExtractorBuilder` are already returning the same position reference for every item in
    // the array if it hasn't changed)
    if (shallowEqual(newNodePositionsForArea, previousNodePositionsForArea)) {
      newNodePositions[id] = previousNodePositionsForArea;
    } else {
      newNodePositions[id] = newNodePositionsForArea;
    }
  });
  if (subConfig.endCallback) {
    subConfig.endCallback(subConfig);
  }

  // If _all_ the node positions for every layout section are the same, return the previous reference
  if (shallowEqual(newNodePositions, previousNodePositions)) {
    return previousNodePositions;
  }
  return newNodePositions;
};
export default calculateGridLayoutTreeNodePositions;